<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useLocalStorage, refDebounced } from '@vueuse/core'
import DeleteUserModal from '@/components/BusinessLogic/Modal/DeleteUserModal.vue'
import UpdateUserModal from '@/components/BusinessLogic/Modal/UpdateUserModal.vue'
import CreateUserModal from '@/components/BusinessLogic/Modal/CreateUserModal.vue'
import ImportUsersModal from '@/components/BusinessLogic/Modal/ImportUsersModal.vue'
import type { UserToUpdate } from '@/components/BusinessLogic/Modal/UpdateUserModal.vue'
import { type MergedUserListItem } from 'shared/src'
import { useDateTime } from '@ai/ui-components'
import { useToast } from '@ai/ui-components/toast'
import { useUsers } from '@/composables/users/useUsers.ts'
import { useUserRoleOptions } from '@/composables/users/useUserRoleOptions'
import { useResourcePermissions } from '@/composables/permissions/useResourcePermissions'
import { STORAGE_KEYS } from '@/enums/StorageEnum'
import { useUsersListQuery, useDeleteUserMutation } from '@/composables/queries'
import ResetFiltersButton from '@/components/ResetFiltersButton.vue'
import { LIST_ACTION_ICONS, ActionButton } from '@/components/BusinessLogic/TableActions'
import { isNotBlank } from '@/utils/String'

interface TableState {
  visibleColumns: string[]
  columnWidths: Record<string, number>
}

const toast = useToast()
interface Props {
  locale: string
}

const props = defineProps<Props>()

const { t, locale: i18nLocale } = useI18n({ useScope: 'global' })
const { formatDateTime } = useDateTime()

const { options: roleOptionsComputed } = useUserRoleOptions()

onBeforeMount(() => {
  i18nLocale.value = props.locale.replace(/[^a-z]/g, '')
})

const statusOptions = computed(() => [
  { label: t('active'), value: 'active' },
  { label: t('inactive'), value: 'inactive' },
])

const searchTerm = useLocalStorage('users-filter-search', '')
const selectedRole = useLocalStorage<string[]>('users-filter-role', [])
const selectedStatus = useLocalStorage<string[]>('users-filter-status', [])
const page = ref(1)
const limit = ref(9999999)

const { createUserModalOpened } = useUsers()
const importUserModalOpened = ref(false)

// Debounce search term to avoid too many API calls
const debouncedSearchTerm = refDebounced(searchTerm, 600)

// Query params - reactive, triggers refetch when changed
const queryParams = computed(() => ({
  q: isNotBlank(debouncedSearchTerm.value) ? debouncedSearchTerm.value : undefined,
  role: selectedRole.value.length ? selectedRole.value : undefined,
  status: selectedStatus.value.length ? selectedStatus.value : undefined,
  page: page.value,
  limit: limit.value,
}))

const { data, isFetching: isLoading } = useUsersListQuery(queryParams)
const { mutateAsync: deleteUser, isPending: isDeleting } = useDeleteUserMutation()

const listUsersPaginated = computed(() => data.value?.users ?? [])

const emit = defineEmits<{
  (e: 'onCreate'): void
  (e: 'onDuplicate' | 'onEdit', id: string): void
}>()

const openUpdateModal = ref(false)
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-redundant-type-constituents
const selectedForUpdate = ref<UserToUpdate | null>(null)

const onEdit = (id: string) => {
  // Find the user to update
  const user = listUsersPaginated.value.find((u) => u.id === id)
  if (user) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    selectedForUpdate.value = {
      id: user.id,
      email: user.email,
      name: isNotBlank(user.name) ? user.name : null,
      role: user.role,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      type: user.type ?? 'user',
    }
    openUpdateModal.value = true
  }
  emit('onEdit', id)
}

const onCreate = () => {
  // emitCustomEvent('onCreate', {})
  emit('onCreate')
}

const listColumns = computed(() => [
  { key: 'name', label: t('name') },
  {
    key: 'email',
    label: t('email'),
  },
  { key: 'created_at', label: t('created_at') },
  { key: 'role', label: t('role') },
  { key: 'status', label: t('status') },
])

const selectedForDelete = ref<MergedUserListItem>()
const openDeleteModal = ref(false)
const toggleDeleteModal = (item: MergedUserListItem) => {
  selectedForDelete.value = item
  openDeleteModal.value = !openDeleteModal.value
}

const getActions = (item: MergedUserListItem) => {
  const { canDelete } = useResourcePermissions(item)

  const actions = []

  if (item.permissions?.update) {
    actions.push({
      label: t('edit'),
      value: 'edit',
      icon: LIST_ACTION_ICONS.edit,
      handler: () => {
        onEdit(item.id)
      },
      dataTestid: 'action-edit',
    })
  }

  if (canDelete.value) {
    actions.push({
      label: t('delete'),
      value: 'delete',
      icon: LIST_ACTION_ICONS.deleteDanger,
      handler: () => {
        toggleDeleteModal(item)
      },
      danger: true,
      dataTestid: 'action-delete',
    })
  }

  return actions
}

const onDeleteUser = async (item: MergedUserListItem): Promise<boolean> => {
  if (!item.id || isDeleting.value) return false

  try {
    await deleteUser({ id: item.id, type: item.type })
    toast.success(t('item_has_been_deleted', [isNotBlank(item.name) ? item.name : item.email]))
    return true
  } catch {
    return false
  } finally {
    openDeleteModal.value = false
  }
}

const hasActiveFilters = computed(() => {
  return searchTerm.value !== '' || selectedRole.value.length > 0 || selectedStatus.value.length > 0
})

const resetFilters = () => {
  searchTerm.value = ''
  selectedRole.value = []
  selectedStatus.value = []
  page.value = 1
}

// Table column widths and OTable tableState persistance
const tableState = ref<TableState>()
const tableStateModel = computed<TableState | undefined>({
  get: () => tableState.value,
  set: (next) => {
    tableState.value = next ? { ...next } : undefined
  },
})
</script>

<template>
  <OTable
    id="users"
    dataTestid="users-list"
    :columns="listColumns"
    :model-value="listUsersPaginated"
    sortable
    :loading="isLoading"
    resizable
    column-resize-mode="indicator"
    :state-persistence-key="STORAGE_KEYS.TABLE_STATE_ORGANIZATION_USERS"
    v-model:tableState="tableStateModel"
  >
    <template #created_at="{ item }">
      <OOverflowTooltip
        :content="formatDateTime({ format: 'dateTime', value: item.created_at, fallback: '-' })"
      >
        {{ formatDateTime({ format: 'dateTime', value: item.created_at, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>

    <template #role="{ item }">
      <OBadge
        v-if="item.role"
        variant="secondary"
        :content="t(`role.${item.role}`).toLowerCase()"
      />
    </template>

    <template #status="{ item }">
      <OBadge
        v-if="item.status"
        variant="primary"
        :color="item.status === 'active' ? 'success' : ''"
        :content="t(item.status)"
      />
    </template>

    <template #filterActions>
      <div class="flex justify-between items-center w-full">
        <div class="flex space-x-3 items-center">
          <OInput
            v-model="searchTerm"
            iconSearch
            :placeholder="t('search')"
            size="S"
            dataTestid="users-search"
            class="w-[264px]"
          />
          <ODropdown
            v-model="selectedRole"
            :options="roleOptionsComputed"
            medium
            multi-select
            :filter-title="t('role')"
            filter
            class="!w-fit"
            dataTestid="users-role"
          />
          <ODropdown
            v-model="selectedStatus"
            :options="statusOptions"
            medium
            multi-select
            :filter-title="t('status')"
            filter
            class="!w-fit"
            dataTestid="users-status"
          />
          <ResetFiltersButton :visible="hasActiveFilters" @reset="resetFilters" />
        </div>
        <div class="flex items-center gap-3 w-full justify-end">
          <OButton size="M" :label="t('create')" @click="onCreate" dataTestid="member-create" />
          <OListActions
            small
            placement="bottom-end"
            data-testid="member-actions"
            :actions="[
              {
                label: t('import'),
                value: 'import',
                handler: () => {
                  importUserModalOpened = true
                },
              },
            ]"
          />
        </div>
      </div>
    </template>

    <template #name="{ item }">
      <div class="flex items-center gap-2 min-w-0">
        <OOverflowTooltip :content="item.name || 'N/A'">
          {{ item.name || 'N/A' }}
        </OOverflowTooltip>
      </div>
    </template>

    <template #email="{ item }">
      <div v-mp-no-track class="min-w-0">
        <OOverflowTooltip :content="item.email || ''">
          {{ item.email || '' }}
        </OOverflowTooltip>
      </div>
    </template>

    <template #actions="{ item }">
      <div class="flex items-center space-x-2">
        <ActionButton
          v-if="item.permissions?.update"
          :tooltip="t('edit')"
          icon="edit"
          icon-data-test="edit-pen"
          @click="onEdit(item.id)"
          data-testid="row-action-edit"
        />
        <OListActions
          v-if="getActions(item).length > 0"
          data-testid="row-action-more"
          small
          :actions="getActions(item)"
        />
      </div>
    </template>
  </OTable>

  <DeleteUserModal
    v-if="openDeleteModal"
    v-model:open="openDeleteModal"
    :item="
      (selectedForDelete as MergedUserListItem)?.name ||
      (selectedForDelete as MergedUserListItem)?.email
    "
    :primary-action-promise="() => onDeleteUser(selectedForDelete as MergedUserListItem)"
    data-testid="row-delete-modal"
  />

  <UpdateUserModal
    v-if="openUpdateModal"
    v-model:open="openUpdateModal"
    :user="selectedForUpdate"
    data-testid="update-user-modal"
  />

  <CreateUserModal v-model:open="createUserModalOpened" data-testid="create-user-modal" />

  <ImportUsersModal v-model:open="importUserModalOpened" data-testid="import-user-modal" />
</template>
